/* ImportSummaryRenderer.js */

"use strict";
const ButtonControl = require("@gluenetworks/steve_ui_core").Control.Action.Button;
const _ = require("lodash");

class ImportSummaryRenderer {
    constructor(model) {
        this._model = model;
        this._rootElem = document.createElement("command-set-import-summary-gl");
        this._rootElem.component = this._model;
        this._logger = this._model.logger;
        this._titlePanel = document.createElement("h2");
        this._titlePanel.classList.add("summary-title");
        this._summaryPanel = document.createElement("div");
        this._summaryPanel.classList.add("summary");
        this._errorPanel = document.createElement("div");
        this._errorPanel.classList.add("import-errors");
        this._errorLabel = document.createElement("div");
        this._errorLabel.classList.add("error-label");
        this._errorLabel.innerHTML = "Errors:";
        this._errorList = document.createElement("div");
        this._errorList.classList.add("error-list");
        this._errorPanel.appendChild(this._errorList);
        this._fillerPanel = document.createElement("div");
        this._fillerPanel.classList.add("filler");
        this._buttonBar = document.createElement("div");
        this._buttonBar.classList.add("summary-buttons");
        this._okButton = new ButtonControl({
            label: "OK",
            darkTheme: true,
        });

        this._registerListeners();
    }

    /********************************************/
    /* START PRIVATE COMPONENT RENDERER METHODS */
    /********************************************/

    _registerListeners() {
        this._okButton.on("UserClick", (this._onOkClick = this._onOkClick.bind(this)));
    }

    _unregisterListeners() {
        this._okButton.removeAllListeners("UserClick");
    }

    /*******************************************/
    /* END PRIVATE COMPONENT RENDERER METHODS  */
    /* START PUBLIC COMPONENT RENDERER METHODS */
    /*******************************************/

    /* jshint ignore:start */
    async shutdown() {
        this._unregisterListeners();
        await this._okButton.shutdown();
        this._summaryPanel = undefined;
        this._errorPanel = undefined;
    }
    /* jshint ignore:end */

    /**
     * Stateless method to completely rebuild this component from the _rootElem down.
     * @returns {Promise} An ES6 promise that lets the caller know when the component has finished rendering. If a non
     * Promise is returned the caller will assume the Renderer has completed rendering.
     */
    render() {
        return new Promise((resolve /*,reject*/) => {
            if (!this._rootElem.hasChildNodes()) {
                this._rootElem.appendChild(this._titlePanel);
                this._rootElem.appendChild(this._summaryPanel);
                this._rootElem.appendChild(this._errorLabel);
                this._rootElem.appendChild(this._errorPanel);
                this._rootElem.appendChild(this._fillerPanel);
                this._rootElem.appendChild(this._buttonBar);
                this._buttonBar.appendChild(this._okButton.renderer.rootElement);
                this._displaySummary();
            }
            resolve();
        });
    }

    get rootElement() {
        return this.getRootElement();
    }

    getRootElement() {
        return this._rootElem;
    }

    get model() {
        return this.getModel();
    }

    getModel() {
        return this._model;
    }

    /*******************************************/
    /* END COMPONENT RENDERER METHODS          */
    /* START PRIVATE RENDER METHODS            */
    /*******************************************/

    /*******************************************/
    /* END PRIVATE RENDER METHODS              */
    /* START HANDLER METHODS                   */
    /*******************************************/

    _onOkClick() {
        this._model.sendOk();
    }

    /*******************************************/
    /* END HANDLER METHODS                     */
    /* START PRIVATE API METHODS               */
    /*******************************************/

    _setTitle() {
        let title = "Import completed";
        this._titlePanel.innerHTML = title;
    }

    _displaySummary() {
        const overallErrorMsg = _.get(this._model.importResults, "errorMsg", undefined);
        const commandSetSummary = _.get(this._model.importResults, "summary.command.stats", {
            errorMsgs: [],
        });
        const regexSummary = _.get(this._model.importResults, "summary.regex.stats", {
            errorMsgs: [],
        });
        let cmdSetFailureCount = 0;
        let regexFailureCount = 0;
        let errorMsgs = [];

        if (_.isNumber(commandSetSummary.failureCount) && commandSetSummary.failureCount > 0) {
            cmdSetFailureCount = parseInt(commandSetSummary.failureCount);
        }

        if (_.isNumber(regexSummary.failureCount) && regexSummary.failureCount > 0) {
            regexFailureCount = parseInt(regexSummary.failureCount);
        }

        if (commandSetSummary.errorMsgs.length > 0 || regexSummary.errorMsgs.length > 0) {
            errorMsgs = commandSetSummary.errorMsgs.concat(regexSummary.errorMsgs);
        }

        this._setTitle();

        while (this._summaryPanel.firstChild) {
            this._summaryPanel.firstChild.remove();
        }

        //Display generic error if any
        if (overallErrorMsg) {
            let genericErrorTitle = document.createElement("span");
            genericErrorTitle.innerText = "Unexpected error";
            genericErrorTitle.classList.add("section-title");
            this._summaryPanel.appendChild(genericErrorTitle);

            let genericErrorMsg = document.createElement("span");
            genericErrorMsg.innerText =
                "There were one or more unexpected errors on importing.  Below is a summary of what was still processed";
            this._summaryPanel.appendChild(genericErrorMsg);

            console.log("Error importing: ", overallErrorMsg);
        }

        let cmdSetTitle = document.createElement("span");
        cmdSetTitle.innerText = "Command Sets";
        cmdSetTitle.classList.add("section-title");
        this._summaryPanel.appendChild(cmdSetTitle);

        let cmdSetCreateCountLabel = document.createElement("span");
        cmdSetCreateCountLabel.innerText = `Created: ${commandSetSummary.createCount || 0}`;
        this._summaryPanel.appendChild(cmdSetCreateCountLabel);

        let cmdSetUpdateCountLabel = document.createElement("span");
        cmdSetUpdateCountLabel.innerText = `Updated: ${commandSetSummary.updateCount || 0}`;
        this._summaryPanel.appendChild(cmdSetUpdateCountLabel);

        let cmdSetRenamedCountLabel = document.createElement("span");
        cmdSetRenamedCountLabel.innerText = `Renamed: ${commandSetSummary.renameCount || 0}`;
        this._summaryPanel.appendChild(cmdSetRenamedCountLabel);

        let cmdSetSkipCountLabel = document.createElement("span");
        cmdSetSkipCountLabel.innerText = `Skipped: ${commandSetSummary.skipCount || 0}`;
        this._summaryPanel.appendChild(cmdSetSkipCountLabel);

        let cmdSetFailureCountLabel = document.createElement("span");
        cmdSetFailureCountLabel.innerText = `Failed: ${cmdSetFailureCount}`;
        this._summaryPanel.appendChild(cmdSetFailureCountLabel);

        let lineFeed = document.createElement("br");
        this._summaryPanel.appendChild(lineFeed);

        let regexTitle = document.createElement("span");
        regexTitle.innerText = "Redaction Expressions";
        regexTitle.classList.add("section-title");
        this._summaryPanel.appendChild(regexTitle);

        let regexCreateCountLabel = document.createElement("span");
        regexCreateCountLabel.innerText = `Created: ${regexSummary.createCount || 0}`;
        this._summaryPanel.appendChild(regexCreateCountLabel);

        let regexUpdateCountLabel = document.createElement("span");
        regexUpdateCountLabel.innerText = `Updated: ${regexSummary.updateCount || 0}`;
        this._summaryPanel.appendChild(regexUpdateCountLabel);

        let regexRenamedCountLabel = document.createElement("span");
        regexRenamedCountLabel.innerText = `Renamed: ${regexSummary.renameCount || 0}`;
        this._summaryPanel.appendChild(regexRenamedCountLabel);

        let regexSkipCountLabel = document.createElement("span");
        regexSkipCountLabel.innerText = `Skipped: ${regexSummary.skipCount || 0}`;
        this._summaryPanel.appendChild(regexSkipCountLabel);

        let regexFailureCountLabel = document.createElement("span");
        regexFailureCountLabel.innerText = `Failed: ${regexFailureCount}`;
        this._summaryPanel.appendChild(regexFailureCountLabel);

        while (this._errorList.firstChild) {
            this._errorList.firstChild.remove();
        }
        if (errorMsgs.length > 0) {
            this._errorLabel.classList.remove("hidden");
            this._errorPanel.classList.remove("hidden");
            this._fillerPanel.classList.add("hidden");
            for (let key in errorMsgs) {
                let errorMsg = document.createElement("div");
                errorMsg.innerText = errorMsgs[key];
                errorMsg.classList.add("error-msg");
                this._errorList.appendChild(errorMsg);
            }
        } else {
            this._errorLabel.classList.add("hidden");
            this._errorPanel.classList.add("hidden");
            this._fillerPanel.classList.remove("hidden");
        }
    }

    /*******************************************/
    /*  END PRIVATE API METHODS                */
    /*******************************************/
}

module.exports = ImportSummaryRenderer;
